import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { styled } from 'styled-components'

import SimulateurWarning from '@/components/SimulateurWarning'
import Simulation from '@/components/Simulation'
import { Body, Button, H3, Li, Strong, Ul } from '@/design-system'
import { usePageMetadata } from '@/hooks/usePageMetadata'
import useSimulationPublicodes from '@/hooks/useSimulationPublicodes'
import { SimulateurId } from '@/hooks/useSimulatorsMetadata'
import { useUrl } from '@/hooks/useUrl'
import { CessationActivitéGoals } from '@/pages/simulateurs/cessation-activité/Goals'
import { indépendantMetadata } from '@/pages/simulateurs/indépendant/metadata'
import { companySituationSelector } from '@/store/selectors/company/companySituation.selector'
import { situationSelector } from '@/store/selectors/simulation/situation/situation.selector'
import { omit } from '@/utils'
import { URSSAF } from '@/utils/logos'
import { EngineProvider } from '@/utils/publicodes/EngineContext'

import SimulateurPageLayout from '../SimulateurPageLayout'
import { cessationActivitéMetadata } from './metadata'
import { cessationActivitéOpenGraph } from './opengraph'
import { configCessationActivité } from './simulationConfig'

const nextSteps = ['indépendant'] satisfies SimulateurId[]

export const CessationActivitéSimulation = () => {
	const metadata = usePageMetadata(cessationActivitéMetadata)
	const { isReady, engine, questions, raccourcis } = useSimulationPublicodes(
		metadata,
		configCessationActivité
	)

	const { t } = useTranslation()

	const externalLinks = [
		{
			url: 'https://www.urssaf.fr/accueil/services/services-independants/cessation-activite.html',
			title: t(
				'pages.simulateurs.cessation-activité.externalLinks.1.title',
				'Le service Cessation d’activité'
			),
			description: t(
				'pages.simulateurs.cessation-activité.externalLinks.1.description',
				'L’Urssaf vous accompagne à toutes les étapes clés de votre démarche de cessation d’activité.'
			),
			logo: URSSAF,
			ctaLabel: t('external-links.service.ctaLabel', 'Accéder au service'),
			ariaLabel: t(
				'external-links.service.ariaLabel',
				'Accéder au service sur urssaf.fr, nouvelle fenêtre'
			),
		},
	]

	const situation = {
		...useSelector(situationSelector),
		...useSelector(companySituationSelector),
	}

	const filteredSituation = omit(situation, 'entreprise . date de cessation')
	const basePath = usePageMetadata(indépendantMetadata).path
	const lien = useUrl({ path: basePath, situation: filteredSituation })

	return (
		<EngineProvider value={engine}>
			<SimulateurPageLayout
				metadata={metadata}
				openGraph={cessationActivitéOpenGraph(t)}
				isReady={isReady}
				nextSteps={nextSteps}
				externalLinks={externalLinks}
			>
				<Simulation
					questionsPublicodes={questions}
					raccourcisPublicodes={raccourcis}
					customSimulationButton={
						<DivWithPaddingBottom>
							<H3>
								{t(
									'pages.simulateurs.cessation-activité.bouton-année-précédente.titre',
									'Avez-vous besoin de calculer les cotisations de l’année précédente ?'
								)}
							</H3>
							<Button size="MD" href={lien}>
								{t(
									'pages.simulateurs.cessation-activité.bouton-année-précédente.libellé',
									'Vos cotisations pour l’année précédente'
								)}
							</Button>
						</DivWithPaddingBottom>
					}
				>
					<SimulateurWarning
						metadata={metadata}
						informationsComplémentaires={
							<Trans i18nKey="pages.simulateurs.cessation-activité.warning">
								<Body>
									Vous êtes travailleur indépendant ou travailleuse indépendante
									et vous souhaitez estimer{' '}
									<Strong>
										les cotisations dues avant de cesser votre activité
									</Strong>
									. Ce simulateur est là pour vous aider. À noter que le montant
									indiqué par ce simulateur est une estimation. Seul le
									«&nbsp;décompte réel de l'Urssaf&nbsp;» vous permettra de
									connaître le montant exact.{' '}
								</Body>
								<Body>
									<Strong>Ce simulateur ne tient pas compte&nbsp;:</Strong>
									<Ul>
										<Li>des versements déjà effectués,</Li>
										<Li>des cotisations provisionnelles déjà appelées,</Li>
										<Li>ni des éventuelles dettes.</Li>
									</Ul>
								</Body>
							</Trans>
						}
					/>
					<CessationActivitéGoals />
				</Simulation>
			</SimulateurPageLayout>
		</EngineProvider>
	)
}

const DivWithPaddingBottom = styled.div`
	padding-bottom: ${({ theme }) => theme.spacings.lg};
`
